// A fullscreen-capable canvas that owns one ERenderer, sized in device pixels.

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { ERenderer } from "../render/eRenderer";

export interface StageHandle {
  renderer: ERenderer | null;
  element: HTMLDivElement | null;
  enterFullscreen(screen?: unknown): Promise<void>;
}

export const Stage = forwardRef<StageHandle, { background?: string; children?: React.ReactNode; onReady?: (r: ERenderer) => void }>(
  function Stage({ background = "#ffffff", children, onReady }, ref) {
    const wrap = useRef<HTMLDivElement>(null);
    const canvas = useRef<HTMLCanvasElement>(null);
    const rendererRef = useRef<ERenderer | null>(null);
    const [error, setError] = useState<string | null>(null);

    useImperativeHandle(ref, () => ({
      get renderer() { return rendererRef.current; },
      get element() { return wrap.current; },
      async enterFullscreen(screen?: unknown) {
        const el = wrap.current;
        if (!el) return;
        const opts = screen ? ({ screen } as FullscreenOptions) : undefined;
        await el.requestFullscreen(opts);
      },
    }), []);

    useEffect(() => {
      const c = canvas.current;
      if (!c) return;
      let r: ERenderer;
      try {
        r = new ERenderer(c, { preferFloat16: true });
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return;
      }
      rendererRef.current = r;
      const ro = new ResizeObserver((entries) => {
        const e = entries[0];
        if (!e) return;
        const box = e.devicePixelContentBoxSize?.[0];
        const w = box ? box.inlineSize : Math.round(e.contentRect.width * devicePixelRatio);
        const h = box ? box.blockSize : Math.round(e.contentRect.height * devicePixelRatio);
        r.resize(Math.max(1, w), Math.max(1, h));
      });
      ro.observe(c, { box: "device-pixel-content-box" as ResizeObserverBoxOptions });
      onReady?.(r);
      return () => ro.disconnect();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <div ref={wrap} className="stage" style={{ background }}>
        <canvas ref={canvas} className="stage-canvas" />
        {error && <div className="stage-error">{error}</div>}
        {children}
      </div>
    );
  });
